import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  PerkLocationResponse,
  PerkLocationRequest,
} from "@/types/generated/cms.generated";

// Fetch all perk locations (hierarchical)
export function usePerkLocations() {
  return useQuery<PerkLocationResponse[]>({
    queryKey: ["perk-locations"],
    queryFn: () => apiRequest("GET", "/perk-locations"),
  });
}

// Create perk location
export function useCreatePerkLocation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: PerkLocationRequest) =>
      apiRequest("POST", "/perk-locations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-locations"] });
      toast({
        title: "Location created",
        description: "Perk location has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create location. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Update perk location
export function useUpdatePerkLocation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PerkLocationRequest }) =>
      apiRequest("PUT", `/perk-locations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-locations"] });
      toast({
        title: "Location updated",
        description: "Perk location has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update location. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Delete perk location
export function useDeletePerkLocation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/perk-locations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-locations"] });
      toast({
        title: "Location deleted",
        description: "Perk location has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete location. Please try again.",
        variant: "destructive",
      });
    },
  });
}
